import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { User, Phone, Briefcase, Heart, Edit2, Save, X, Loader2 } from 'lucide-react';

export function ClientProfileTab({ caseInfo, editMode, setEditMode, formData, setFormData, saving, onSave, formatDate }) {
  const renderField = (label, field, type = 'text', testId) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      {editMode ? (
        <Input
          type={type}
          value={formData[field] || ''}
          onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
          data-testid={testId}
        />
      ) : (
        <p className="font-body p-2 bg-secondary/50 rounded">
          {type === 'date' ? formatDate(caseInfo[field]) : (caseInfo[field] || 'Not provided')}
        </p>
      )}
    </div>
  );

  const renderSelect = (label, field, options, testId) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      {editMode ? (
        <Select value={formData[field] || ''} onValueChange={(val) => setFormData({ ...formData, [field]: val })}>
          <SelectTrigger data-testid={testId}>
            <SelectValue placeholder={`Select ${label.toLowerCase()}`} />
          </SelectTrigger>
          <SelectContent>
            {options.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <p className="font-body p-2 bg-secondary/50 rounded capitalize">{caseInfo[field] || 'Not provided'}</p>
      )}
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Personal Information
            </CardTitle>
            <CardDescription>Update your information to help us process your case faster</CardDescription>
          </div>
          {!editMode ? (
            <Button variant="outline" onClick={() => setEditMode(true)} data-testid="edit-profile-btn">
              <Edit2 className="w-4 h-4 mr-2" />
              Edit Profile
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditMode(false)} data-testid="cancel-edit-btn">
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={onSave} disabled={saving} data-testid="save-profile-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Basic Information */}
        <div>
          <h3 className="font-body font-medium mb-4 flex items-center gap-2">
            <User className="w-4 h-4 text-primary" />
            Basic Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderField('Full Name', 'name', 'text', 'input-name')}
            {renderField('Date of Birth', 'dob', 'date', 'input-dob')}
            {renderField('Country of Origin', 'country', 'text', 'input-country')}
            {renderSelect('Marital Status', 'marital_status', [
              { value: 'single', label: 'Single' },
              { value: 'married', label: 'Married' },
              { value: 'divorced', label: 'Divorced' },
              { value: 'widowed', label: 'Widowed' },
              { value: 'separated', label: 'Separated' },
            ], 'input-marital-status')}
          </div>
        </div>

        {/* Contact Information */}
        <div>
          <h3 className="font-body font-medium mb-4 flex items-center gap-2">
            <Phone className="w-4 h-4 text-primary" />
            Contact Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderField('Phone Number', 'telephone', 'text', 'input-phone')}
            {renderField('Email Address', 'email', 'email', 'input-email')}
            <div className="space-y-2 md:col-span-2">
              <Label>Current Address</Label>
              {editMode ? (
                <Input value={formData.address || ''} onChange={(e) => setFormData({ ...formData, address: e.target.value })} data-testid="input-address" />
              ) : (
                <p className="font-body p-2 bg-secondary/50 rounded">{caseInfo.address || 'Not provided'}</p>
              )}
            </div>
            {renderSelect('Preferred Language', 'preferred_language', [
              { value: 'english', label: 'English' },
              { value: 'spanish', label: 'Spanish' },
              { value: 'portuguese', label: 'Portuguese' },
              { value: 'french', label: 'French' },
              { value: 'chinese', label: 'Chinese' },
              { value: 'other', label: 'Other' },
            ], 'input-language')}
          </div>
        </div>

        {/* Employment Information */}
        <div>
          <h3 className="font-body font-medium mb-4 flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-primary" />
            Employment Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderField('Occupation', 'occupation', 'text', 'input-occupation')}
            {renderField('Employer Name', 'employer_name', 'text', 'input-employer')}
            <div className="space-y-2 md:col-span-2">
              <Label>Employer Address</Label>
              {editMode ? (
                <Input value={formData.employer_address || ''} onChange={(e) => setFormData({ ...formData, employer_address: e.target.value })} data-testid="input-employer-address" />
              ) : (
                <p className="font-body p-2 bg-secondary/50 rounded">{caseInfo.employer_address || 'Not provided'}</p>
              )}
            </div>
          </div>
        </div>

        {/* Emergency Contact */}
        <div>
          <h3 className="font-body font-medium mb-4 flex items-center gap-2">
            <Heart className="w-4 h-4 text-primary" />
            Emergency Contact
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderField('Contact Name', 'emergency_contact_name', 'text', 'input-emergency-name')}
            {renderField('Contact Phone', 'emergency_contact_phone', 'text', 'input-emergency-phone')}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
